import React, { lazy, Suspense, useMemo, useState } from 'react';
import { MessageSquare, RefreshCw } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getAssistantRouteContext } from '../../utils/assistantRouteContext';
import { requestAssistantOpen } from '../../utils/assistantLauncher';
import { isFounderSupportView, readFounderSupportSession } from '../../utils/founderSupportSession';
import ErrorBoundary from './ErrorBoundary';
import './GlobalAssistant.css';

const ChatWidget = lazy(() => import('./ChatWidget'));

function useAssistantContext() {
  const { isLoggedIn, user } = useAuth();
  const { pathname, search } = useLocation();
  if (!isLoggedIn || !(user?.canonicalUserId || user?.id)
    || readFounderSupportSession() || isFounderSupportView()) return null;
  return getAssistantRouteContext(pathname, search);
}

export const MobileAssistantLauncher: React.FC = () => {
  const context = useAssistantContext();
  const { t } = useTranslation();
  if (!context) return null;
  return (
    <button
      type="button"
      className="topbar-mobile-toggle"
      data-testid="mobile-chat-launcher"
      aria-label={t('chatOpen')}
      title={t('chatOpen')}
      onClick={requestAssistantOpen}
    >
      <MessageSquare size={20} aria-hidden="true" />
    </button>
  );
};

const GlobalAssistant: React.FC = () => {
  const context = useAssistantContext();
  const { t } = useTranslation();
  const [retry, setRetry] = useState(0);
  const Widget = useMemo(() => retry === 0 ? ChatWidget : lazy(() => import('./ChatWidget')), [retry]);
  if (!context) return null;
  return (
    <ErrorBoundary key={retry} fallback={
      <aside className="global-assistant-error" role="alert" aria-label={t('chatAssistantTitle')}>
        <strong>{t('chatAssistantTitle')}</strong>
        <p>{t('errorGenericMessage')}</p>
        <button type="button" onClick={() => {
          requestAssistantOpen();
          setRetry(value => value + 1);
        }}>
          <RefreshCw size={18} aria-hidden="true" />{t('chatContextRetry')}
        </button>
      </aside>
    }>
      <Suspense fallback={null}><Widget context={context} mobileHeaderLauncher /></Suspense>
    </ErrorBoundary>
  );
};

export default GlobalAssistant;
